// A point query owns its results, independently of raster tiles and other clicks.
export function createPointQuerySession(config) {
    let current = null;
    let version = 0;

    function cancel() {
        if (!current) return;
        current.sources.forEach(source => {
            clearTimeout(source.timer);
            if (source.controller) source.controller.abort();
        });
        current = null;
        config.onCancel();
    }

    function merge(session, hits) {
        (hits || []).filter(Boolean).forEach(hit => {
            const key = hit.type + ':' + hit.id;
            const index = session.hits.findIndex(item => item.type + ':' + item.id === key);
            if (index < 0) session.hits.push(hit);
            else session.hits[index] = hit;
        });
    }

    function publish(session) {
        if (current !== session) return;
        session.loading = session.sources.some(source => source.state === 'loading');
        config.onUpdate(session);
    }

    function run(session, source) {
        clearTimeout(source.timer);
        if (source.controller) source.controller.abort();
        const controller = new AbortController();
        source.controller = controller;
        source.state = 'loading';
        const isCurrent = () => current === session && source.controller === controller;
        source.timer = setTimeout(() => {
            if (!isCurrent()) return;
            source.state = 'error';
            controller.abort();
            publish(session);
        }, config.timeoutMs || 15000);
        if (!source.request) return;
        // Start requests in descriptor order: local lookups first, external last.
        Promise.resolve().then(() => {
            if (!isCurrent() || controller.signal.aborted) return [];
            return source.request(controller.signal);
        }).then(hits => {
            if (!isCurrent() || controller.signal.aborted) return;
            merge(session, hits);
            source.state = session.hits.some(hit => source.types.includes(hit.type)) ? 'success' : 'empty';
        }).catch(() => {
            if (!isCurrent() || controller.signal.aborted) return;
            source.state = 'error';
        }).finally(() => {
            if (!isCurrent()) return;
            clearTimeout(source.timer);
            publish(session);
        });
    }

    function start(latlng, point, seedHits = []) {
        cancel();
        const session = { id: ++version, latlng, point, hits: [], sources: [], loading: true };
        current = session;
        merge(session, config.collectHits(point, latlng));
        merge(session, seedHits);
        session.sources = config.getSources(latlng).map(source => ({ ...source, state: 'loading' }));
        config.onStart(session);
        session.sources.forEach(source => run(session, source));
        refresh();
        return session;
    }

    function refresh() {
        const session = current;
        if (!session) return;
        merge(session, config.collectHits(session.point, session.latlng));
        session.sources.forEach(source => {
            if (source.request || source.state !== 'loading' || !source.getState) return;
            const state = source.getState();
            if (state === 'loading' || state === 'idle') return;
            source.state = state === 'error' ? 'error'
                : session.hits.some(hit => source.types.includes(hit.type)) ? 'success' : 'empty';
            clearTimeout(source.timer);
        });
        publish(session);
    }

    function retry(key) {
        const session = current;
        const source = session && session.sources.find(item => item.key === key);
        if (!source || source.state !== 'error') return;
        run(session, source);
        if (source.reload) source.reload();
        publish(session);
    }

    return { start, cancel, refresh, retry, get: () => current };
}
